import React from 'react';
import showdown from "showdown";

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    CircularProgress,
    Stack,
    Card,
    CardContent,
    Typography,
    Chip,
    Box,
    Divider,
    IconButton,
    Tooltip,
} from "@mui/joy";

// components
import BaseApi from "../../utils/BaseApi";
import AudioPlayer from "./AudioPlayer";
import ComponentCard from "../Common/ComponentCard";
import CommonCopyToClipboard from "../Common/CommonCopyToClipboard";
import LeadCard from "../Cards/LeadCard";
import UserSubscriberContext from "../../context/UserSubscriberContext";

// icons
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import PhoneIcon from '@mui/icons-material/Phone';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import dayjs from "dayjs";


class CallCard extends React.Component {
    static contextType = UserSubscriberContext

    constructor(props) {
        super(props);
        this.state = {
            call: this.props.call,
            textRequested: false,
            sound: null,
            soundRequested: false,
            communications: null,
            leads: null,
        };
    }

    async componentDidMount() {
        if (this.props.showCommunications){
            await this.retrieveCommunications();
            await this.retrieveLeads();
        }
    }

    async retrieveCommunications() {
        const url = `communication/`;
        const params = {
            standard_content: this.state.call.phone,
            type: 'phone',
            page_size: 10000,
        };
        const api = new BaseApi();
        let response = await api.get(
            url,
            params
        );
        await this.setState(
            {
                communications: response.data.results
            }
        );
    }

    async retrieveLeads() {
        const url = `lead/`;
        const params = {
            standard_phone: this.state.call.phone,
            page_size: 10000,
        };
        const api = new BaseApi();
        let response = await api.get(
            url,
            params
        );
        this.setState(
            {
                leads: response.data.results
            }
        );
    }

    async retrieveCall() {
        const url = `sunsim_call/${this.props.call.id}`;
        const api = new BaseApi();
        let response = await api.get(
            url,
            {}
        );
        await this.setState(
            {
                call: response.data
            }
        );
    }

    async retrieveSound() {
        this.setState(
            {
                soundRequested: true,
            }
        );
        const url = `sunsim_call/${this.props.call.id}/record_sound/`;
        const api = new BaseApi();
        let response = await api.get(
            url,
            {}
        );
        if (response.status === 200){
            this.setState(
                {
                    sound: response.data.content
                }
            );
        }
        this.setState(
            {
                soundRequested: false,
            }
        );
    }

    getCallDirection(call) {
        if (!call.content) return 'unknown';
        const direction = call.content.direction;
        if (direction === 'inbound') return 'inbound';
        if (direction === 'outbound') return 'outbound';
        return 'unknown';
    }

    getCallStatus(call) {
        if (!call.content) return 'unknown';
        const status = call.content.callState;
        if (status === 'completed') return 'completed';
        if (status === 'missed') return 'missed';
        if (status === 'no_answer') return 'no_answer';
        return 'unknown';
    }

    getDirectionChip(call) {
        const direction = this.getCallDirection(call);
        
        if (direction === 'inbound') {
            return (
                <Chip
                    size="sm"
                    variant="soft"
                    color="success"
                    startDecorator={<CallReceivedIcon fontSize="small"/>}
                >
                    Входящий
                </Chip>
            );
        } else if (direction === 'outbound') {
            return (
                <Chip
                    size="sm"
                    variant="soft"
                    color="primary"
                    startDecorator={<CallMadeIcon fontSize="small"/>}
                >
                    Исходящий
                </Chip>
            );
        }
        return null;
    }

    getIconColor(call) {
        const direction = this.getCallDirection(call);
        const status = this.getCallStatus(call);
        
        if (status === 'missed' || status === 'no_answer') {
            return 'danger';
        }
        
        if (direction === 'inbound') {
            return 'success';
        }
        return 'primary';
    }

    render() {
        if (!this.state.call){
            return (<CircularProgress/>);
        }

        const call = this.state.call;
        const iconColor = this.getIconColor(call);
        
        return (
            <Card size="sm" variant="soft">
                <Accordion>
                    <AccordionSummary
                        indicator={
                            call.call_record_id || call.record_text ? <ArrowDropDownIcon/> : <></>
                        }
                    >
                        <Stack 
                            spacing={1.5} 
                            direction="row" 
                            sx={{
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            {/* Icon circle */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: 'background.level2',
                            }}>
                                <PhoneIcon color={iconColor}/>
                            </Box>

                            {/* Phone and direction */}
                            <Stack flex={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {this.props.showPhone ? (
                                        <Typography level="body-lg" sx={{fontWeight: 'bold'}}>
                                            <CommonCopyToClipboard content={call.phone}/>
                                            {' '}
                                            {call.phone}
                                        </Typography>
                                    ) : (
                                        <Typography level="body-lg" sx={{fontWeight: 'bold'}}>
                                            {call.phone || 'Неизвестный номер'}
                                        </Typography>
                                    )}
                                    {this.getDirectionChip(call)}
                                </Stack>
                                
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography level="body-sm" color="neutral">
                                        <AccessTimeIcon fontSize="small" sx={{mr: 0.5, verticalAlign: 'middle'}}/>
                                        {call.date_time ? dayjs(call.date_time).format('DD.MM.YYYY HH:mm') : 'Неизвестно'}
                                    </Typography>
                                    {call.content?.duration && (
                                        <Typography level="body-sm" color="neutral">
                                            ⏱ {Math.floor(call.content.duration / 60)}:{String(call.content.duration % 60).padStart(2, '0')}
                                        </Typography>
                                    )}
                                    {call.content?.receiverName && (
                                        <Typography level="body-sm" color="neutral">
                                            📞 {call.content.receiverName}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>

                            {/* Status indicators */}
                            <Stack direction="row" spacing={1}>
                                {call.call_record_id && (
                                    <Tooltip title="Есть запись разговора">
                                        <FiberManualRecordIcon color="danger" sx={{fontSize: 12}}/>
                                    </Tooltip>
                                )}
                                {call.record_text && (
                                    <Tooltip title="Есть транскрипция">
                                        <TextSnippetIcon color="primary" sx={{fontSize: 16}}/>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{pt: 0}}>
                        <Stack spacing={2}>
                            {/* Related communications and leads */}
                            {this.props.showCommunications && this.state.communications && this.state.leads && (
                                (this.state.communications.length > 0 || this.state.leads.length > 0) && (
                                    <Stack spacing={1}>
                                        <Typography level="body-sm" color="neutral" sx={{fontWeight: 'bold'}}>
                                            Связанные объекты
                                        </Typography>
                                        <Stack 
                                            direction="row" 
                                            spacing={1} 
                                            sx={{
                                                overflow: 'auto',
                                                pb: 1,
                                            }}
                                        >
                                            {this.state.communications.map((c, index) => (
                                                <ComponentCard
                                                    key={`comm-${index}`}
                                                    entity={c.related_app}
                                                    entity_id={c.related_app_id}
                                                    mini={true}
                                                />
                                            ))}
                                            {this.state.leads.map((l, index) => (
                                                <LeadCard
                                                    key={`lead-${index}`}
                                                    lead={l}
                                                    mini={true}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                )
                            )}

                            {/* Audio recording */}
                            {call.call_record_id && (
                                <Card size="sm" variant="outlined">
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Typography level="title-sm">
                                                🎙 Запись разговора
                                            </Typography>
                                            {this.state.sound ? (
                                                <AudioPlayer
                                                    base64String={this.state.sound}
                                                    extension={'mp3'}
                                                />
                                            ) : this.state.soundRequested ? (
                                                <Chip size="sm" color="neutral">
                                                    Запущен запрос
                                                </Chip>
                                            ) : (
                                                <Chip
                                                    size="sm"
                                                    color="primary"
                                                    variant="soft"
                                                    onClick={() => this.retrieveSound()}
                                                    sx={{cursor: 'pointer'}}
                                                >
                                                    Запросить запись
                                                </Chip>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Transcript */}
                            {call.record_text && call.record_text.replace(' ', '') !== '' && (
                                <Card size="sm" variant="outlined">
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Typography level="title-sm">
                                                📝 Транскрипция
                                            </Typography>
                                            <Divider/>
                                            <Box sx={{
                                                bgcolor: 'background.level1',
                                                p: 2,
                                                borderRadius: 1,
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                            }}>
                                                <Typography level="body-sm" sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    lineHeight: 1.5,
                                                }}>
                                                    {call.record_text}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </Card>
        );
    }
}

export default CallCard;
